import { ChangeEvent, ComponentProps, useEffect, useState } from "react";
import { Combobox } from "@headlessui/react";
import { useFormContext } from "react-hook-form";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";

import InputContainer from "@/components/forms/controls/InputContainer";
import { cn } from "@/helpers/className";
import { useGoogleMapsApi } from "@/helpers/googleMapsApi";
import useFormError from "@/hooks/useFormError";
import { AllowedLanguages } from "@/types/generic";

interface PostalCodeInputProps
  extends Omit<ComponentProps<"input">, "id" | "aria-describedby"> {
  className?: string;
  disabled?: boolean;
  hiddenLabel?: boolean;
  id?: string;
  i18n: {
    no_results: string;
  };
  initialValue?: string;
  inputClassName?: string;
  inputName: string;
  label: string;
  labelClasses?: string;
  locale: AllowedLanguages;
  placeholder: string;
  required?: boolean;
}

export const PostalCodeInput = ({
  className,
  disabled,
  hiddenLabel = false,
  i18n,
  initialValue,
  inputClassName,
  inputName,
  label,
  labelClasses,
  locale,
  placeholder,
  required,
  ...props
}: PostalCodeInputProps) => {
  const [query, setQuery] = useState("");
  const { gmapsApiIsLoaded } = useGoogleMapsApi();
  const name = inputName;
  const id = props.id ?? name;

  const {
    watch,
    setValue: setInputValue,
    register,
    formState: { errors },
  } = useFormContext();
  const error = useFormError(inputName, errors);

  const registerInput = register(inputName);

  const {
    init,
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    initOnMount: false,
    requestOptions: {
      language: locale,
      region: "be",
      componentRestrictions: {
        country: "be",
      },
      types: ["locality", "postal_code", "sublocality"],
    },
    debounce: 300,
  });

  const watchInput = watch(inputName) as string;

  // Mainly used to update the query on page load
  useEffect(() => {
    setQuery(watchInput ?? "");
  }, [watchInput]);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue, false);
      setInputValue(inputName, initialValue);
      setQuery(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    if (gmapsApiIsLoaded) {
      init();
    }
  }, [gmapsApiIsLoaded, init]);

  const transformedData = data.map((suggestion) => {
    const {
      place_id,
      structured_formatting: { main_text, secondary_text },
    } = suggestion;

    return {
      place_id,
      main_text,
      secondary_text,
    };
  });

  const handleInput = (e: ChangeEvent) => {
    const value = (e.target as HTMLInputElement).value.toString() || "";
    // Update query for Google Maps API
    setValue(value);
    setInputValue(inputName, value);
  };

  const handleSelect = (suggestion: string) => {
    const selectedSuggestion = transformedData.find(
      (item) => item.main_text === suggestion,
    );

    if (!selectedSuggestion?.place_id) return false;

    getGeocode({ placeId: selectedSuggestion?.place_id })
      .then((results) => {
        const postalCodeComponent = results[0].address_components.find(
          (component) => component.types.includes("postal_code"),
        );
        const postalCode = postalCodeComponent?.short_name;

        if (postalCode) {
          setValue(postalCode, false);
          setInputValue(inputName, postalCode);
          setQuery(postalCode);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    clearSuggestions();
  };

  return (
    <InputContainer className={className} isError={Boolean(error)}>
      <label
        className={cn(
          labelClasses,
          hiddenLabel && "sr-only",
          required && "label-required",
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <Combobox
        as={"div"}
        className="relative"
        onChange={handleSelect}
        value={query}
        disabled={disabled}
      >
        <div className="flex w-full">
          <Combobox.Input
            placeholder={placeholder}
            className={cn("form-input", inputClassName)}
            autoComplete={"off"}
            id={id}
            data-error={error ? "true" : undefined}
            onBlur={(e) => {
              setQuery(e.target?.value.toString() || "");
            }}
            onChange={handleInput}
            required={required}
          />
          <input type="hidden" {...registerInput} />
          <Combobox.Options
            className={cn(
              "absolute inset-x-0 top-full z-20 max-h-72 scroll-py-2 overflow-y-auto border bg-white px-6 py-2 text-sm text-gray-800 shadow-lg",
            )}
          >
            {transformedData.length === 0 && watchInput ? (
              <div className="text-black-400">{i18n.no_results}</div>
            ) : (
              transformedData.map(({ main_text, secondary_text, place_id }) => (
                <Combobox.Option
                  key={place_id}
                  value={`${main_text}`}
                  className={({ active }) =>
                    cn(
                      "cursor-pointer select-none border-b py-2 transition-colors last:border-b-0",
                      active && "text-green",
                    )
                  }
                >
                  <strong>{main_text}</strong> <span>{secondary_text}</span>
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </div>
        {error && <div className="form-error">{error}</div>}
      </Combobox>
    </InputContainer>
  );
};

export default PostalCodeInput;
